<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import LeadCaptureAPI from 'dashboard/api/leadCaptureEndpoints';

const props = defineProps({
  endpoint: { type: Object, default: null },
  inboxes: { type: Array, required: true },
});
const emit = defineEmits(['saved', 'close']);
const { t } = useI18n();

const isEdit = computed(() => !!props.endpoint?.id);

const form = ref({
  name: '',
  endpoint_type: 'wa_redirect',
  inbox_id: null,
  message_template: '',
  send_proactive: false,
  active: true,
  rate_limit_per_minute: 15,
  capture_config: {
    gclid: true,
    fbclid: true,
    ctwa_clid: true,
    utms: true,
    private_note: false,
    private_note_intro: '',
    qualification_rules: [],
    qualified_label: '',
    disqualified_label: '',
    skip_proactive_if_unqualified: false,
    meta_page_id: '',
    meta_form_id: '',
    meta_fields: [],
  },
  origin_allowlist: [],
});
const allowlistInput = ref('');
const imageFile = ref(null);
const removeImage = ref(false);
const saving = ref(false);

watch(() => props.endpoint, ep => {
  if (!ep) return;
  form.value = {
    name: ep.name,
    endpoint_type: ep.endpoint_type,
    inbox_id: ep.inbox_id,
    message_template: ep.message_template || '',
    send_proactive: ep.send_proactive,
    active: ep.active,
    rate_limit_per_minute: ep.rate_limit_per_minute,
    capture_config: {
      gclid: true,
      fbclid: true,
      ctwa_clid: true,
      utms: true,
      private_note: false,
      private_note_intro: '',
      qualified_label: '',
      disqualified_label: '',
      skip_proactive_if_unqualified: false,
      meta_page_id: '',
      meta_form_id: '',
      ...ep.capture_config,
      qualification_rules: [...(ep.capture_config?.qualification_rules || [])],
      meta_fields: [...(ep.capture_config?.meta_fields || [])],
    },
    origin_allowlist: [...(ep.origin_allowlist || [])],
  };
  allowlistInput.value = (ep.origin_allowlist || []).join(', ');
}, { immediate: true });

const whatsappInboxes = computed(() => props.inboxes.filter(
  i => i.channel_type === 'Channel::Whatsapp' || i.channel_type === 'Channel::Api' || i.channel_type === 'Channel::WhatsappWeb'
));
const selectedInbox = computed(() => whatsappInboxes.value.find(i => i.id === form.value.inbox_id));
const inboxIsCloudApi = computed(() => {
  const ib = selectedInbox.value;
  return ib?.channel_type === 'Channel::Whatsapp' && ib?.provider === 'whatsapp_cloud';
});

const supportsProactive = computed(() => {
  const ib = selectedInbox.value;
  return ib && (ib.channel_type === 'Channel::Api' || ib.channel_type === 'Channel::WhatsappWeb');
});
watch(supportsProactive, val => { if (!val) form.value.send_proactive = false; });
watch(() => form.value.endpoint_type, val => { if (val === 'wa_redirect') form.value.send_proactive = false; });

async function save() {
  if (!form.value.name?.trim()) return useAlert(t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.NAME_REQUIRED'));
  if (!form.value.inbox_id) return useAlert(t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.INBOX_REQUIRED'));
  saving.value = true;
  try {
    form.value.origin_allowlist = (allowlistInput.value || '').split(/[, ]+/).map(s => s.trim()).filter(Boolean);
    if (isEdit.value) {
      await LeadCaptureAPI.updateWithImage(props.endpoint.id, form.value, imageFile.value, removeImage.value);
    } else {
      await LeadCaptureAPI.createWithImage(form.value, imageFile.value);
    }
    useAlert(isEdit.value ? t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.UPDATED') : t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.CREATED'));
    emit('saved');
  } catch (e) {
    useAlert(e?.response?.data?.error || e?.response?.data?.message || t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.SAVE_ERROR'));
  } finally {
    saving.value = false;
  }
}

function onFile(e) {
  imageFile.value = e.target.files?.[0] || null;
  if (imageFile.value) removeImage.value = false;
}

// SiteUp v309: importa os campos do formulario Meta Lead Ads pra montar a
// qualificacao por opcao sem digitar as chaves na mao.
const importing = ref(false);
const optionFields = computed(
  () => (form.value.capture_config.meta_fields || []).filter(f => (f.options || []).length)
);

async function importMetaFields() {
  const formId = (form.value.capture_config.meta_form_id || '').trim();
  if (!formId) return useAlert(t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.IMPORT_NEED_ID'));
  importing.value = true;
  try {
    const { data } = await LeadCaptureAPI.getFormFields(formId);
    form.value.capture_config.meta_fields = data.fields || [];
    const count = (data.fields || []).length;
    useAlert(data.name
      ? t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.IMPORTED_FROM', { count, name: data.name })
      : t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.IMPORTED', { count }));
  } catch (e) {
    useAlert(e?.response?.data?.error || t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.IMPORT_ERROR'));
  } finally {
    importing.value = false;
  }
}

// As opcoes marcadas como qualificadas viram UMA regra 'in' por campo, que o
// backend avalia com value_list. Separador = quebra de linha (NAO virgula):
// opcoes de formulario podem conter virgula (ex.: "Sim, mais de 1 ano").
function qualifyingOptions(fieldKey) {
  const rule = form.value.capture_config.qualification_rules.find(r => r.field === fieldKey && r.op === 'in');
  return rule ? (rule.value || '').split('\n').map(s => s.trim()).filter(Boolean) : [];
}
function isOptionQualifying(fieldKey, option) {
  return qualifyingOptions(fieldKey).includes(option);
}
function toggleOption(fieldKey, option) {
  const rules = form.value.capture_config.qualification_rules;
  let opts = qualifyingOptions(fieldKey);
  opts = opts.includes(option) ? opts.filter(o => o !== option) : [...opts, option];
  const idx = rules.findIndex(r => r.field === fieldKey && r.op === 'in');
  if (opts.length === 0) {
    if (idx >= 0) rules.splice(idx, 1);
  } else if (idx >= 0) {
    rules[idx].value = opts.join('\n');
  } else {
    rules.push({ field: fieldKey, op: 'in', value: opts.join('\n') });
  }
}

const messagePlaceholder = computed(() => form.value.endpoint_type === 'wa_redirect'
  ? t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.PREFILLED_PLACEHOLDER')
  : t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.PROACTIVE_PLACEHOLDER', { name: '{nome}' }));
const proactiveHint = computed(() => t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.PROACTIVE_HINT', {
  fields: '{nome}, {first_name}, {email}, {telefone}',
  example: '{valor_conta}',
}));
</script>

<template>
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-n-alpha-black2 backdrop-blur-[4px]" @click.self="$emit('close')">
    <div class="bg-n-background rounded-xl w-[90%] max-w-2xl max-h-[90vh] flex flex-col shadow-xl">
      <header class="flex justify-between items-center px-6 py-4 border-b border-n-slate-4">
        <h3 class="text-base font-semibold text-n-slate-12">{{ isEdit ? $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.TITLE_EDIT') : $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.TITLE_NEW') }}</h3>
        <button type="button" class="inline-flex items-center justify-center size-11 text-n-slate-11 hover:text-n-slate-12" :aria-label="$t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.CLOSE')" @click="$emit('close')"><span class="i-ri-close-line size-5" /></button>
      </header>

      <div class="px-6 py-5 overflow-y-auto flex-1">
        <label class="block mb-4">
          <span class="block text-xs font-medium text-n-slate-12 mb-1">{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.NAME') }}</span>
          <input v-model="form.name" type="text" :placeholder="$t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.NAME_PLACEHOLDER')"
            class="w-full px-3 py-2 rounded-md border border-n-slate-4 bg-n-background text-n-slate-12 text-sm focus:outline-none focus:border-n-brand" />
        </label>

        <label class="block mb-4">
          <span class="block text-xs font-medium text-n-slate-12 mb-1">{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.TYPE') }}</span>
          <select v-model="form.endpoint_type" :disabled="isEdit"
            class="w-full px-3 py-2 rounded-md border border-n-slate-4 bg-n-background text-n-slate-12 text-sm">
            <option value="wa_redirect">{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.TYPE_WA_REDIRECT') }}</option>
            <option value="webhook">{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.TYPE_WEBHOOK') }}</option>
          </select>
          <small class="block mt-1 text-xs text-n-slate-11">
            {{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.TYPE_HINT_WA_REDIRECT') }}<br>
            {{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.TYPE_HINT_WEBHOOK') }}
          </small>
        </label>

        <label class="block mb-4">
          <span class="block text-xs font-medium text-n-slate-12 mb-1">{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.INBOX') }}</span>
          <select v-model="form.inbox_id"
            class="w-full px-3 py-2 rounded-md border border-n-slate-4 bg-n-background text-n-slate-12 text-sm">
            <option :value="null">{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.INBOX_PLACEHOLDER') }}</option>
            <option v-for="i in whatsappInboxes" :key="i.id" :value="i.id">
              {{ i.name }} ({{ i.channel_type === 'Channel::Whatsapp' ? $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.INBOX_OFFICIAL') : $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.INBOX_UNOFFICIAL') }})
            </option>
          </select>
        </label>

        <!-- Mensagem template visivel APENAS pro tipo Botao WhatsApp.
             No tipo Webhook, a mensagem so faz sentido se WAHA + proativa,
             entao foi movida pra dentro do bloco proativo abaixo. -->
        <label v-if="form.endpoint_type === 'wa_redirect'" class="block mb-4">
          <span class="block text-xs font-medium text-n-slate-12 mb-1">{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.PREFILLED_MESSAGE') }}</span>
          <textarea v-model="form.message_template" rows="3" :placeholder="messagePlaceholder"
            class="w-full px-3 py-2 rounded-md border border-n-slate-4 bg-n-background text-n-slate-12 text-sm"></textarea>
          <small class="block mt-1 text-xs text-n-slate-11">{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.PREFILLED_MESSAGE_HINT') }}</small>
        </label>


        <fieldset class="border border-n-slate-4 rounded-md p-3 mb-4">
          <legend class="text-xs px-2 text-n-slate-11">{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.CAPTURE') }}</legend>
          <label class="block py-1 text-sm text-n-slate-12"><input type="checkbox" v-model="form.capture_config.gclid" class="mr-2"> gclid (Google Ads)</label>
          <label class="block py-1 text-sm text-n-slate-12"><input type="checkbox" v-model="form.capture_config.fbclid" class="mr-2"> fbclid (Meta)</label>
          <label class="block py-1 text-sm text-n-slate-12"><input type="checkbox" v-model="form.capture_config.ctwa_clid" class="mr-2"> ctwa_clid (Click-to-WhatsApp)</label>
          <label class="block py-1 text-sm text-n-slate-12"><input type="checkbox" v-model="form.capture_config.utms" class="mr-2"> UTMs</label>
        </fieldset>

        <!-- SiteUp Phase B: integracao Meta Lead Ads (recebe leads automaticamente) -->
        <fieldset class="border border-n-slate-4 rounded-md p-3 mb-4">
          <legend class="text-xs px-2 text-n-slate-11">{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.META') }}</legend>
          <label class="block mb-2">
            <span class="block text-xs font-medium text-n-slate-12 mb-1">{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.META_PAGE_ID') }}</span>
            <input v-model="form.capture_config.meta_page_id" type="text" :placeholder="$t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.META_PAGE_ID_PLACEHOLDER')"
              class="w-full px-3 py-2 rounded-md border border-n-slate-4 bg-n-background text-n-slate-12 text-sm" />
            <small class="block mt-1 text-xs text-n-slate-11">{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.META_PAGE_ID_HINT') }}</small>
          </label>
          <label class="block">
            <span class="block text-xs font-medium text-n-slate-12 mb-1">{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.META_FORM_ID') }}</span>
            <div class="flex items-center gap-2">
              <input v-model="form.capture_config.meta_form_id" type="text" :placeholder="$t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.META_FORM_ID_PLACEHOLDER')"
                class="flex-1 px-3 py-2 rounded-md border border-n-slate-4 bg-n-background text-n-slate-12 text-sm" />
              <button type="button" :disabled="importing"
                class="px-3 py-2 rounded-md border border-n-brand text-n-brand text-sm whitespace-nowrap hover:bg-n-brand/10 disabled:opacity-50"
                @click="importMetaFields">
                {{ importing ? $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.IMPORTING') : $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.IMPORT') }}
              </button>
            </div>
            <small class="block mt-1 text-xs text-n-slate-11">{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.IMPORT_HINT') }}</small>
          </label>
        </fieldset>

        <!-- SiteUp Phase A: nota privada com as respostas do formulario -->
        <fieldset class="border border-n-slate-4 rounded-md p-3 mb-4">
          <legend class="text-xs px-2 text-n-slate-11">{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.PRIVATE_NOTE') }}</legend>
          <label class="flex items-center gap-2 py-1 text-sm text-n-slate-12">
            <input type="checkbox" v-model="form.capture_config.private_note" />
            {{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.PRIVATE_NOTE_TOGGLE') }}
          </label>
          <input v-if="form.capture_config.private_note" v-model="form.capture_config.private_note_intro"
            type="text" :placeholder="$t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.PRIVATE_NOTE_PLACEHOLDER')"
            class="w-full mt-2 px-3 py-2 rounded-md border border-n-slate-4 bg-n-background text-n-slate-12 text-sm" />
          <small class="block mt-1 text-xs text-n-slate-11">{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.PRIVATE_NOTE_HINT') }}</small>
        </fieldset>

        <!-- SiteUp Phase A: qualificacao por regras -->
        <fieldset class="border border-n-slate-4 rounded-md p-3 mb-4">
          <legend class="text-xs px-2 text-n-slate-11">{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.QUALIFICATION') }}</legend>

          <!-- SiteUp v309: qualificacao por opcao (a partir dos campos importados) -->
          <div v-if="optionFields.length" class="mb-3 space-y-2">
            <p class="text-xs text-n-slate-11">{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.QUALIFY_BY_OPTION') }}</p>
            <div v-for="f in optionFields" :key="f.key" class="border border-n-slate-3 rounded p-2">
              <div class="text-xs font-medium text-n-slate-12 mb-1">{{ f.label || f.key }}</div>
              <label v-for="opt in f.options" :key="opt" class="flex items-center gap-2 py-0.5 text-sm text-n-slate-12">
                <input type="checkbox" :checked="isOptionQualifying(f.key, opt)" @change="toggleOption(f.key, opt)" />
                {{ opt }}
              </label>
            </div>
          </div>

          <details class="mb-2">
            <summary class="text-xs text-n-slate-11 cursor-pointer select-none">{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.MANUAL_RULES') }}</summary>
            <small class="block mt-2 mb-2 text-xs text-n-slate-11">{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.MANUAL_RULES_HINT') }}</small>
          <div v-for="(rule, i) in form.capture_config.qualification_rules" :key="i" class="flex items-center gap-1 mb-2">
            <input v-model="rule.field" type="text" :placeholder="$t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.RULE_FIELD')" :aria-label="$t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.RULE_FIELD')"
              class="w-32 px-2 py-1.5 text-sm rounded border border-n-slate-4 bg-n-background text-n-slate-12" />
            <select v-model="rule.op" :aria-label="$t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.RULE_OPERATOR')" class="px-1 py-1.5 text-sm rounded border border-n-slate-4 bg-n-background text-n-slate-12">
              <option value="equals">=</option>
              <option value="not_equals">≠</option>
              <option value="contains">{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.OP_CONTAINS') }}</option>
              <option value="gt">&gt;</option>
              <option value="gte">≥</option>
              <option value="lt">&lt;</option>
              <option value="lte">≤</option>
              <option value="in">{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.OP_IN') }}</option>
              <option value="not_in">{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.OP_NOT_IN') }}</option>
            </select>
            <textarea v-if="rule.op === 'in' || rule.op === 'not_in'" v-model="rule.value" rows="2" :placeholder="$t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.RULE_VALUES')" :aria-label="$t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.RULE_VALUE')"
              class="flex-1 px-2 py-1.5 text-sm rounded border border-n-slate-4 bg-n-background text-n-slate-12"></textarea>
            <input v-else v-model="rule.value" type="text" :placeholder="$t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.RULE_VALUE')" :aria-label="$t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.RULE_VALUE')"
              class="flex-1 px-2 py-1.5 text-sm rounded border border-n-slate-4 bg-n-background text-n-slate-12" />
            <button type="button" class="inline-flex items-center justify-center size-11 text-n-slate-11 hover:text-n-ruby-11" :aria-label="$t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.REMOVE_RULE')" @click="form.capture_config.qualification_rules.splice(i, 1)"><span class="i-ri-close-line size-4" /></button>
          </div>
          <button type="button" class="text-xs text-n-brand"
            @click="form.capture_config.qualification_rules.push({ field: '', op: 'equals', value: '' })">
            <span class="i-ri-add-line size-3" /> {{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.ADD_RULE') }}
          </button>
          </details>
          <div v-if="form.capture_config.qualification_rules.length" class="grid grid-cols-2 gap-2 mt-3">
            <label class="block">
              <span class="block text-xs text-n-slate-11 mb-1">{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.QUALIFIED_LABEL') }}</span>
              <input v-model="form.capture_config.qualified_label" type="text" :placeholder="$t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.QUALIFIED_LABEL_PLACEHOLDER')"
                class="w-full px-2 py-1.5 text-sm rounded border border-n-slate-4 bg-n-background text-n-slate-12" />
            </label>
            <label class="block">
              <span class="block text-xs text-n-slate-11 mb-1">{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.DISQUALIFIED_LABEL') }}</span>
              <input v-model="form.capture_config.disqualified_label" type="text" :placeholder="$t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.DISQUALIFIED_LABEL_PLACEHOLDER')"
                class="w-full px-2 py-1.5 text-sm rounded border border-n-slate-4 bg-n-background text-n-slate-12" />
            </label>
          </div>
          <label class="flex items-center gap-2 mt-3 pt-3 border-t border-n-slate-3 text-sm text-n-slate-12">
            <input type="checkbox" v-model="form.capture_config.skip_proactive_if_unqualified" />
            <span>{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.SKIP_IF_UNQUALIFIED') }} <small class="text-n-slate-11">{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.SKIP_IF_UNQUALIFIED_HINT') }}</small></span>
          </label>
        </fieldset>

        <div v-if="form.endpoint_type === 'webhook' && selectedInbox" class="bg-n-slate-2 rounded-md p-3 mb-4">
          <div v-if="inboxIsCloudApi" class="text-xs text-n-amber-11 bg-n-amber-3 p-2.5 rounded">
            {{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.CLOUD_API_NOTICE') }}
          </div>
          <div v-else-if="supportsProactive" class="text-sm text-n-slate-12">
            <label class="flex items-center gap-2 mb-2">
              <input type="checkbox" v-model="form.send_proactive" />
              <span>{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.PROACTIVE_TOGGLE') }}</span>
            </label>
            <div v-if="form.send_proactive" class="pl-6 mt-2 space-y-3">
              <label class="block">
                <span class="block text-xs font-medium text-n-slate-12 mb-1">{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.PROACTIVE_MESSAGE') }}</span>
                <textarea v-model="form.message_template" rows="3" :placeholder="messagePlaceholder"
                  class="w-full px-3 py-2 rounded-md border border-n-slate-4 bg-n-background text-n-slate-12 text-sm"></textarea>
                <small class="block mt-1 text-xs text-n-slate-11">{{ proactiveHint }}</small>
              </label>
              <label class="block">
                <span class="block text-xs font-medium text-n-slate-12 mb-1">{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.WELCOME_IMAGE') }}</span>
                <input type="file" accept="image/*" @change="onFile" class="text-xs" />
                <small v-if="endpoint?.welcome_image_url && !imageFile" class="block mt-1 text-xs text-n-slate-11">
                  {{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.CURRENT_IMAGE') }} <a :href="endpoint.welcome_image_url" target="_blank" rel="noopener" class="text-n-brand">{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.VIEW_IMAGE') }}</a>
                  <label class="ml-2"><input type="checkbox" v-model="removeImage" class="mr-1"> {{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.REMOVE_IMAGE') }}</label>
                </small>
              </label>
            </div>
          </div>
        </div>

        <label class="block mb-4">
          <span class="block text-xs font-medium text-n-slate-12 mb-1">{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.ALLOWED_DOMAINS') }}</span>
          <input v-model="allowlistInput" type="text" :placeholder="$t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.ALLOWED_DOMAINS_PLACEHOLDER')"
            class="w-full px-3 py-2 rounded-md border border-n-slate-4 bg-n-background text-n-slate-12 text-sm" />
          <small class="block mt-1 text-xs text-n-slate-11">{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.ALLOWED_DOMAINS_HINT') }}</small>
        </label>

        <label class="block mb-4">
          <span class="block text-xs font-medium text-n-slate-12 mb-1">{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.RATE_LIMIT') }}</span>
          <input v-model.number="form.rate_limit_per_minute" type="number" min="1" max="600"
            class="w-full px-3 py-2 rounded-md border border-n-slate-4 bg-n-background text-n-slate-12 text-sm" />
        </label>

        <label class="flex items-center gap-2 text-sm text-n-slate-12">
          <input type="checkbox" v-model="form.active" />
          <span>{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.ACTIVE') }}</span>
        </label>
      </div>

      <footer class="px-6 py-4 border-t border-n-slate-4 flex justify-end gap-2">
        <button class="px-4 py-2 rounded-md border border-n-slate-4 text-sm text-n-slate-12 hover:bg-n-slate-3" @click="$emit('close')">{{ $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.CANCEL') }}</button>
        <button class="px-4 py-2 rounded-md bg-n-brand text-white text-sm hover:bg-n-brand/90 disabled:opacity-50" :disabled="saving" @click="save">
          {{ saving ? $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.SAVING') : (isEdit ? $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.SAVE') : $t('INTEGRATION_SETTINGS.LEAD_CAPTURE.FORM.CREATE')) }}
        </button>
      </footer>
    </div>
  </div>
</template>
